import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  CircularProgress
} from '@mui/material';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface PropertyType {
  id: string;
  name: string;
  description: string;
  active: boolean;
  isCommercial: boolean;
}

interface PropertyTypeDialogProps {
  open: boolean;
  propertyType: PropertyType | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface PropertyTypeFormData {
  name: string;
  description: string;
  active: boolean;
  isCommercial: boolean;
}

const PropertyTypeDialog: React.FC<PropertyTypeDialogProps> = ({
  open,
  propertyType,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<PropertyTypeFormData>({
    name: '',
    description: '',
    active: true,
    isCommercial: false
  });
  const [errors, setErrors] = useState<Partial<PropertyTypeFormData>>({});
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useAppDispatch();

  const isEditing = !!propertyType;

  // Reset form when dialog opens/closes or property type changes
  useEffect(() => {
    if (open) {
      if (propertyType) {
        setFormData({
          name: propertyType.name,
          description: propertyType.description,
          active: propertyType.active,
          isCommercial: propertyType.isCommercial
        });
      } else {
        setFormData({
          name: '',
          description: '',
          active: true,
          isCommercial: false
        });
      }
      setErrors({});
    }
  }, [open, propertyType]);

  // Handle input changes
  const handleInputChange = (field: keyof PropertyTypeFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = (field === 'active' || field === 'isCommercial') ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Partial<PropertyTypeFormData> = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Property type name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Property type name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Property type name cannot exceed 100 characters';
    }

    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 5) {
      newErrors.description = 'Description must be at least 5 characters';
    } else if (formData.description.trim().length > 500) {
      newErrors.description = 'Description cannot exceed 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const payload: any = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        isCommercial: formData.isCommercial
      };

      // Add id for updates
      if (isEditing) {
        payload.id = propertyType!.id;
      }

      const endpoint = isEditing ? 'api/property-types/update' : 'api/property-types/create';
      const response = await DataService.post(endpoint, payload);

      if (response.ok) {
        onSuccess();
      } else {
        // Try to get error message from response
        let errorMessage = `Failed to ${isEditing ? 'update' : 'create'} property type`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.errors) {
            // Handle validation errors
            const validationErrors = Object.values(errorData.errors).flat();
            errorMessage = validationErrors.join(', ');
          }
        } catch (e) {
          // Use default error message
        }

        dispatch(
          showAlert({
            message: errorMessage,
            type: 'error'
          })
        );
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: `Network error. Please check your connection and try again.`,
          type: 'error'
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Typography variant="h6" component="div">
            {isEditing ? 'Edit Property Type' : 'Add New Property Type'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEditing
              ? 'Update the property type information below'
              : 'Fill in the details to create a new property type'
            }
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField
            label="Property Type Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            disabled={submitting}
            fullWidth
            placeholder="Enter property type name (e.g., Apartment, House, Office)"
            variant="outlined"
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={handleInputChange('description')}
            error={!!errors.description}
            helperText={errors.description}
            disabled={submitting}
            fullWidth
            multiline
            rows={4}
            placeholder="Describe this property type and its characteristics"
            variant="outlined"
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.isCommercial}
                onChange={handleInputChange('isCommercial')}
                disabled={submitting}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  {formData.isCommercial ? 'Commercial' : 'Residential'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formData.isCommercial
                    ? 'This property type is for commercial use'
                    : 'This property type is for residential use'
                  }
                </Typography>
              </Box>
            }
          />

          {isEditing && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={handleInputChange('active')}
                  disabled={submitting}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {formData.active ? 'Active' : 'Inactive'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.active
                      ? 'This property type is currently active and available for use'
                      : 'This property type is inactive and will not be available for selection'
                    }
                  </Typography>
                </Box>
              }
            />
          )}

          {/* Information for new property types */}
          {!isEditing && (
            <Box
              sx={{
                p: 2,
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: 1,
                border: '1px solid rgba(0, 0, 0, 0.12)'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                Property Type Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Once created, this property type will be available for selection in property forms.
                Common types include Apartment, House, Office, Warehouse, Commercial, etc.
              </Typography>
            </Box>
          )}
        </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            {submitting
              ? `${isEditing ? 'Updating' : 'Creating'}...`
              : `${isEditing ? 'Update' : 'Create'} Property Type`
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PropertyTypeDialog;